/* eslint-disable max-len */
import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  Typography,
  Box,
  TablePagination,
  CircularProgress
} from '@mui/material';
import { Visibility } from '@mui/icons-material';
import useListagemCifaStore from 'stores/cifa/listagemCifa.store';
import { ICifaForm } from 'stores/cifa/cifa.store';
import { CifaStatusEnum } from 'enum/cifaStatus.enum';

interface TableCifaProps {
  onViewCifa: (cifa: ICifaForm) => void;
  onPageChange: (page: number, limit: number) => void;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case CifaStatusEnum.NOT_STARTED:
      return 'default';
    case CifaStatusEnum.PENDING:
      return 'warning';
    case CifaStatusEnum.WAITING_FOR_APPROVAL:
      return 'info';
    case CifaStatusEnum.APPROVED:
      return 'success';
    case CifaStatusEnum.REJECTED:
      return 'error';
    default:
      return 'default';
  }
};

const getStatusLabel = (status: string) => {
  switch (status) {
    case CifaStatusEnum.NOT_STARTED:
      return 'NÃO INICIADO';
    case CifaStatusEnum.PENDING:
      return 'PENDENTE';
    case CifaStatusEnum.WAITING_FOR_APPROVAL:
      return 'ENVIADO AO ESTADO';
    case CifaStatusEnum.APPROVED:
      return 'APROVADO';
    case CifaStatusEnum.REJECTED:
      return 'REJEITADO';
    default:
      return status;
  }
};

const formatDate = (dateString: string | null) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('pt-BR');
};

export const TableCifa: React.FC<TableCifaProps> = ({ onViewCifa, onPageChange }) => {
  const { listCifas, loading, meta } = useListagemCifaStore();

  const handleChangePage = (event: unknown, newPage: number) => {
    onPageChange(newPage + 1, meta.itemsPerPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newLimit = parseInt(event.target.value, 10);
    onPageChange(1, newLimit);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (listCifas.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          Nenhum município iniciou o preenchimento do CIFA 2026 até o momento.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Município</strong></TableCell>
              <TableCell><strong>Status do preenchimento</strong></TableCell>
              <TableCell><strong>Data de envio</strong></TableCell>
              <TableCell><strong>Responsável</strong></TableCell>
              <TableCell align="center"><strong>Ação</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {listCifas.map((cifa) => (
              <TableRow key={cifa.uuid} hover>
                <TableCell>
                  <Typography variant="body1">
                    {/* Assumindo que o nome do município vem da instituição */}
                    {cifa.institution.name || 'Nome do Município'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={getStatusLabel(cifa.formulario_status || CifaStatusEnum.NOT_STARTED)}
                    color={getStatusColor(cifa.formulario_status || CifaStatusEnum.NOT_STARTED)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {(cifa.formulario_status === CifaStatusEnum.WAITING_FOR_APPROVAL || cifa.formulario_status === CifaStatusEnum.APPROVED)
                    ? formatDate(null) // Aqui seria a data de envio quando disponível
                    : '-'
                  }
                </TableCell>
                <TableCell>
                  {cifa.responsavel?.name || '-'}
                </TableCell>
                <TableCell align="center">
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Visibility />}
                    onClick={() => onViewCifa(cifa)}
                    disabled={cifa.formulario_status === CifaStatusEnum.NOT_STARTED}
                  >
                    Visualizar
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={meta.totalItems}
        page={meta.currentPage - 1}
        onPageChange={handleChangePage}
        rowsPerPage={meta.itemsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50]}
        labelRowsPerPage="Itens por página:"
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
        }
      />
    </Paper>
  );
};

export default TableCifa;
